import domready from "domready";
import { getJSONFromAttribute } from "../Shared/Utils";
import { createRoot } from "react-dom/client";
import React, { useEffect, useState } from "react";
import { currentLanguage, getStrings } from "../../Shared/Reservation/StringResources";
import { Alert, Card, Col, Row } from "react-bootstrap";
import { SpinnerSmall } from "../../Shared/Components/Spinner";
import { IBookingSummary } from "../Models";
import dayjs from "dayjs";
import { SegmentInformation, TravelBaseInformation } from "../Components/Booking/TravelSummary";
import { Price } from "../Components/Price";
import { trackTransaction } from "../../Shared/Tracking";

let containerElement: HTMLElement;
const strings = getStrings().component.booking;

interface IPaymentReturnData
{
	bookingNumber: string;
	pspReference: string;
	accessToken: string;
	apiUrl: string;
	status?: "accepted" | "declined";
	managePage?: { url: string, title: string }
}

interface IReceipt
{
	paymentMethod: string;
	paid: boolean;
	booking: IBookingSummary;
}

const App = (props: IPaymentReturnData) => {

	const [receipt, setReceipt] = useState<IReceipt>();
	const [error, setError] = useState<string>();

	const loadReceipt = async () => {
		setError(undefined);
		const response = await fetch(`${props.apiUrl}/booking/${props.bookingNumber}/receipt/${props.pspReference}` , {
			method: "GET",
			headers: {
				"Accept": "application/json",
				"Accept-Language": currentLanguage(),
				"Authorization": `Bearer ${props.accessToken}`
			}
		});

		if (response.ok)
		{
			const data = await response.json() as IReceipt;
			setReceipt(data);

			if (data.paid && data.booking?.outbound)
				trackTransaction(
					data.booking.bookingNumber,
					data.booking.amountPaid,
					0, //TODO: Tax
					[{
						name: `${data.booking.outbound.from.name} - ${data.booking.outbound.to.name}, ${data.booking.inbound ? "Tur och retur" : "Enkel"}`,
						price: data.booking.amountPaid,
						category: data.booking.outbound.from.name
					}]
			)
		}
		else
		{
			setError(`${strings.unableToLoad}: ${response.status} ${response.statusText}`);
		}
	};


	useEffect(() => {
		if (!receipt) {
			loadReceipt();
		}
		else if (!receipt.paid) {
			setTimeout(loadReceipt, 2000);
		}
	}, [receipt]);


	return <>
		{ error && <Alert variant="danger"><strong>Fel!</strong> {error}</Alert> }

		{ !receipt?.paid && receipt?.paymentMethod === "KLARNA" && <Alert variant="warning">
			<strong>{strings.klarnaInfoHeading}</strong>
			<p dangerouslySetInnerHTML={{ __html: strings.klarnaInfo }}></p>
		</Alert>}

		{ !error && !receipt?.paid && <Alert variant="info">{strings.loading} <SpinnerSmall /></Alert> }
		{ receipt?.paid && <Receipt booking={receipt.booking} managePage={props.managePage} /> }
	</>
}

const Receipt = (props: {booking: IBookingSummary, managePage?: { url: string, title: string }}) => {
	return <>
		<Card>
			<Card.Header>{strings.bookingHeader}</Card.Header>
			<Card.Body>
				<dl>
					<dt>{strings.bookingNumber}</dt>
					<dd>{props.booking.bookingNumber}</dd>
					<dt>{strings.created}</dt>
					<dd>{dayjs(props.booking.created).format("YYYY-MM-DD HH:mm")}</dd>
				</dl>
				<Row>
					{props.booking.outbound && <Col sm={6}>
						<section>
							<h2>{strings.outbound}</h2>
							<div className={props.booking.outbound?.cancelled ? "text-muted" : ""}>
								<TravelBaseInformation travel={props.booking.outbound} direction="outbound" />

								{props.booking.outbound?.segments?.map((segment) => <div className="segment-summary" key={segment.segmentId}>
									<SegmentInformation segment={segment} />
								</div>)}
							</div>
						</section>
					</Col>}
					{props.booking.inbound && <Col sm={6}>
						<section>
							<h2>{strings.inbound}</h2>
							<div className={props.booking.inbound?.cancelled ? "text-muted" : ""}>
								<TravelBaseInformation travel={props.booking.inbound} direction="inbound" />

								{props.booking.inbound?.segments?.map((segment) => <div className="segment-summary" key={segment.segmentId}>
									<SegmentInformation segment={segment} />
								</div>)}
							</div>
						</section>
					</Col>}
				</Row>

				<hr />

				{ props.booking.amountPaid === props.booking.totalPrice ?
				<h4>{strings.totalPrice}: <Price amount={props.booking.totalPrice} /></h4> :
				<>
					<h4>{strings.amountPaid}: <Price amount={props.booking.amountPaid} /></h4>
					<h6>{strings.totalPrice}: <Price amount={props.booking.totalPrice} /></h6>
				</>}
			</Card.Body>
		</Card>


		{ props.managePage &&
			<p className="mt-3">För att se och göra ändringar på din bokning, gå till <a href={`${props.managePage.url}?orderId=${props.booking.bookingNumber}`}>{props.managePage.title}</a>.</p> }
		{/* <Alert variant="info" className="mt-3">{strings.s3BookingInfo}</Alert> */}
	</>
}

domready(() => {
	containerElement = document.getElementById("booking-payment-return");

	if (!containerElement)
		return;

	const settings = getJSONFromAttribute<IPaymentReturnData>(containerElement, "data-settings", undefined);

	// if (settings.status === "declined")
	// {
	// 	//Trigga manage-appen istället, för att kunna hantera betalning igen
	// 	setTimeout(() => {
	// 		const loadBookingEvent = new CustomEvent("LoadBooking", { detail: {
	// 			bookingNumber: settings.bookingNumber,
	// 			mode: "PaymentOnly"
	// 		}});
	// 		window.dispatchEvent(loadBookingEvent);
	// 	}, 250);
	// }
	// else {
	const root = createRoot(containerElement);
	root.render(<App {...settings} />);
	// }
});